/**
 * Backup Section — consolidated command center (Spec 201 — FR-040)
 *
 * The single "Backup" area with sub-navigation (Dashboard / Plans / Schedules /
 * Restore / Storage) that replaces the ~12 scattered legacy backup routes. Built
 * entirely from the canonical kit (@/components/ui) + design tokens per the design
 * blueprint (Constitution XLV). Foundational shell — the Plans sub-area is functional
 * (plan list + lifecycle); the remaining sub-areas are filled by their pillar phases.
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 201-backup-management-overhaul
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Database, Info, Activity, Trash2, Pause, Play, Lock, Unlock, RefreshCw } from 'lucide-react';
import {
  PageLayout,
  Card,
  Tabs,
  DataTable,
  Badge,
  Button,
  Checkbox,
  Select,
  EmptyState,
  LoadingState,
  ErrorState,
} from '@/components/ui';
import { usePlans } from '../hooks/usePlans';
import { useAuth } from '../hooks/useAuth';
import { ListFilterBar, textMatch } from '../components/common/ListFilterBar';
import { plansApi } from '../api/plans';
import type { BackupPlan, BackupPlanDetail } from '../api/plans';
import { PlanFormModal } from '../components/plans/PlanFormModal';
import { GraphicalScheduleBuilder } from '../components/schedules/GraphicalScheduleBuilder';
import { RestoreBrowser } from '../components/restore/RestoreBrowser';
import { StorageAssignment } from '../components/storage/StorageAssignment';
import { PlanAssignment } from '../components/management/PlanAssignment';
import { CommandCenter } from '../components/dashboard/CommandCenter';
import { BackupHealthArea } from '../components/health/BackupHealthArea';
import { BackupErrorsArea } from '../components/errors/BackupErrorsArea';
import { RecoverabilityArea } from '../components/recoverability/RecoverabilityArea';
import { ReportsArea } from '../components/reports/ReportsArea';
import { CostControlsArea } from '../components/cost/CostControlsArea';
import { ProtectionTiersSettings } from '../components/settings/ProtectionTiersSettings';
import { RetentionPoliciesSettings } from '../components/settings/RetentionPoliciesSettings';
import { useBackupActivity, selectInflightCount } from '../store/backupActivityStore';
import { formatEastern } from '@/utils/dateFormatter';

type SubArea = 'dashboard' | 'plans' | 'management' | 'schedules' | 'restore' | 'storage' | 'health' | 'errors' | 'recoverability' | 'reports' | 'settings' | 'cost';

const TABS: { key: SubArea; label: string }[] = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'plans', label: 'Plans' },
  { key: 'management', label: 'Management' },
  { key: 'schedules', label: 'Schedules' },
  { key: 'restore', label: 'Restore' },
  { key: 'storage', label: 'Backups' },
  { key: 'health', label: 'Health' },
  { key: 'errors', label: 'Errors' },
  { key: 'recoverability', label: 'Recoverability' },
  { key: 'reports', label: 'Reports' },
  { key: 'settings', label: 'Settings' },
  { key: 'cost', label: 'Cost & Controls' },
];

const statusVariant: Record<string, 'success' | 'warning' | 'info' | 'default'> = {
  active: 'success',
  paused: 'warning',
  draft: 'info',
  archived: 'default',
};

function PlansArea({ onEdit }: { onEdit: (id: string) => void }): React.ReactElement {
  const { data: plans, isLoading, isError, refetch } = usePlans();
  const { isOperatorOrAbove, isAdmin } = useAuth();
  const canManage = isOperatorOrAbove();
  // Spec 214 FR-028: legal hold is ADMIN-only server-side — don't render it for Operators
  // (their clicks 403'd while the allSettled runner reported nothing).
  const canHold = isAdmin();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [typeFilter, setTypeFilter] = useState(''); // backup type: lakehouse | warehouse | workspace
  const [hold, setHold] = useState(''); // legal hold: held | none
  const planTypes = (p: BackupPlan) =>
    (p.targetTypes ?? '').toLowerCase().split(',').map((s) => s.trim()).filter(Boolean);
  const filtered = useMemo(
    () =>
      (plans ?? []).filter(
        (p) =>
          (!status || p.status === status) &&
          (!typeFilter || planTypes(p).includes(typeFilter)) &&
          (!hold || (hold === 'held' ? p.legalHold : !p.legalHold)) &&
          textMatch(q, p.name, p.status, p.targetTypes ?? '')
      ),
    [plans, q, status, typeFilter, hold]
  );

  const handleDelete = async (e: React.MouseEvent, p: BackupPlan) => {
    e.stopPropagation();
    if ((p.activeBackupCount ?? 0) > 0) return;
    if (!window.confirm(`Delete plan "${p.name}"? Its ${p.targetCount ?? 0} target assignment(s) will be removed. This can't be undone.`)) return;
    await plansApi.remove(p.id);
    await qc.invalidateQueries({ queryKey: ['backup', 'backup-configs'] });
    refetch();
  };

  // Multi-select + bulk lifecycle / legal-hold across plans.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const toggleSel = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const allShownSelected = filtered.length > 0 && filtered.every((p) => selected.has(p.id));
  const toggleAllShown = () =>
    setSelected((s) => {
      const n = new Set(s);
      if (allShownSelected) filtered.forEach((p) => n.delete(p.id));
      else filtered.forEach((p) => n.add(p.id));
      return n;
    });
  // Spec 214 FR-029: bulk results are INSPECTED and surfaced — the old allSettled-and-forget
  // meant an entire failed batch (e.g. 403s) finished silently with no error and no change.
  const [bulkResult, setBulkResult] = useState<string | null>(null);
  const runBulk = async (fn: (id: string) => Promise<unknown>) => {
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const results = await Promise.allSettled([...selected].map(fn));
      const failed = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
      const ok = results.length - failed.length;
      if (failed.length > 0) {
        const first = failed[0].reason;
        const msg = first instanceof Error ? first.message : String(first);
        setBulkResult(`${ok} succeeded · ${failed.length} failed — ${msg}`);
      } else {
        setBulkResult(`${ok} succeeded`);
      }
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['backup', 'backup-configs'] }),
        qc.invalidateQueries({ queryKey: ['backup', 'schedule-spread'] }),
      ]);
      if (failed.length === 0) setSelected(new Set());
      refetch();
    } finally {
      setBulkBusy(false);
    }
  };
  const bulkTransition = (s: 'pause' | 'activate' | 'archive') => runBulk((id) => plansApi.transition(id, s));
  const bulkHold = (hold: boolean) => runBulk((id) => plansApi.setLegalHold(id, hold));

  if (isLoading) return <LoadingState rows={5} />;
  if (isError) return <ErrorState onRetry={() => refetch()} />;
  if (!plans || plans.length === 0) {
    return (
      <EmptyState
        title="No backup plans yet"
        message="A Backup Plan binds a scoped target set to a schedule, retention policy, and destination — the durable, compliance-grade protection object."
      />
    );
  }

  // Canonical shape per the design blueprint: PageLayout → Card → DataTable.
  // Per-column widths keep the metadata columns compact so the table reads as a
  // content-sized list (not edge-to-edge sprawl); `name` takes the remaining space.
  return (
    <>
      <ListFilterBar query={q} onQuery={setQ} placeholder="Search plans…" count={filtered.length} total={plans.length}>
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          options={[
            { value: '', label: 'All statuses' },
            { value: 'active', label: 'Active' },
            { value: 'paused', label: 'Paused' },
            { value: 'draft', label: 'Draft' },
            { value: 'archived', label: 'Archived' },
          ]}
        />
        <Select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          options={[
            { value: '', label: 'All backup types' },
            { value: 'lakehouse', label: 'Lakehouse' },
            { value: 'warehouse', label: 'Warehouse' },
            { value: 'workspace', label: 'Workspace' },
          ]}
        />
        <Select
          value={hold}
          onChange={(e) => setHold(e.target.value)}
          options={[
            { value: '', label: 'Any legal hold' },
            { value: 'held', label: 'On legal hold' },
            { value: 'none', label: 'Not held' },
          ]}
        />
      </ListFilterBar>
      {canManage && selected.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-[var(--color-accent)] bg-[var(--color-bg-subtle)] px-3 py-2">
          <span className="text-sm font-medium text-[var(--color-text-primary)]">{selected.size} selected</span>
          <span className="grow" />
          <Button variant="outline" size="sm" disabled={bulkBusy} onClick={() => bulkTransition('pause')}><Pause className="h-4 w-4" /> Pause</Button>
          <Button variant="outline" size="sm" disabled={bulkBusy} onClick={() => bulkTransition('activate')}><Play className="h-4 w-4" /> Activate</Button>
          {canHold && (
            <>
              <Button variant="outline" size="sm" disabled={bulkBusy} onClick={() => bulkHold(true)}><Lock className="h-4 w-4" /> Legal hold</Button>
              <Button variant="outline" size="sm" disabled={bulkBusy} onClick={() => bulkHold(false)}><Unlock className="h-4 w-4" /> Release hold</Button>
            </>
          )}
          <Button variant="ghost" size="sm" disabled={bulkBusy} onClick={() => setSelected(new Set())}>Clear</Button>
        </div>
      )}
      {bulkResult && (
        <div className="mb-3 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-3 py-2 text-sm text-[var(--color-text-secondary)]">
          {bulkResult}
        </div>
      )}
      <Card padding="none">
      <DataTable<BackupPlan>
        minWidth="62rem"
        columns={[
          {
            key: 'select',
            header: canManage ? <Checkbox checked={allShownSelected} onChange={toggleAllShown} aria-label="Select all plans" /> : '',
            width: '2.5rem',
            sortable: false,
            render: (p) =>
              canManage ? (
                <span onClick={(e) => e.stopPropagation()}>
                  <Checkbox checked={selected.has(p.id)} onChange={() => toggleSel(p.id)} aria-label={`Select ${p.name}`} />
                </span>
              ) : null,
          },
          { key: 'name', header: 'Plan', sortable: true },
          {
            key: 'status',
            header: 'Status',
            width: '7rem',
            render: (p) => (
              <Badge variant={statusVariant[p.status] ?? 'default'}>{p.status}</Badge>
            ),
          },
          { key: 'targetCount', header: 'Targets', width: '6rem', align: 'right', sortable: true, render: (p) => p.targetCount ?? 0 },
          {
            key: 'backupCount',
            // Spec 214 FR-017: usable backups — succeeded runs (LH/WH) + restorable
            // definition captures incl. Partial (workspace); failed/recycle-binned excluded.
            header: (
              <span title="Usable backups: succeeded runs (lakehouse/warehouse) + restorable definition captures incl. Partial (workspace). Failed and recycle-binned runs don't count.">
                Backups
              </span>
            ),
            width: '9rem',
            align: 'right',
            sortable: true,
            render: (p) => (
              <span className="inline-flex items-center gap-1.5">
                {p.backupCount ?? 0}
                {(p.activeBackupCount ?? 0) > 0 && <Badge variant="info">{p.activeBackupCount} active</Badge>}
              </span>
            ),
          },
          {
            key: 'backupType',
            header: 'Backup type',
            width: '11rem',
            sortable: true,
            sortValue: (p) => p.targetTypes ?? '',
            render: (p) => {
              const types = planTypes(p);
              if (!types.length) return <span className="text-[var(--color-text-secondary)]">—</span>;
              const meta: Record<string, { label: string; variant: 'info' | 'success' | 'warning' }> = {
                lakehouse: { label: 'Lakehouse', variant: 'info' },
                warehouse: { label: 'Warehouse', variant: 'success' },
                workspace: { label: 'Workspace', variant: 'warning' },
              };
              return (
                <span className="inline-flex flex-wrap gap-1">
                  {types.map((t) => {
                    const m = meta[t] ?? { label: t, variant: 'info' as const };
                    return <Badge key={t} variant={m.variant}>{m.label}</Badge>;
                  })}
                </span>
              );
            },
          },
          {
            key: 'updatedAt',
            header: 'Updated',
            width: '12rem',
            render: (p) => formatEastern(p.updatedAt),
          },
          {
            key: 'actions',
            header: '',
            width: '5rem',
            align: 'right',
            sortable: false,
            render: (p) =>
              canManage ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => handleDelete(e, p)}
                  disabled={(p.activeBackupCount ?? 0) > 0}
                  title={(p.activeBackupCount ?? 0) > 0 ? 'Has active backups — cannot delete' : 'Delete plan'}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              ) : null,
          },
        ]}
        rows={filtered}
        rowKey={(p) => p.id}
        onRowClick={(p) => onEdit(p.id)}
      />
      </Card>
    </>
  );
}

export const BackupSection: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const active = (TABS.find((t) => t.key === params.tab)?.key ?? 'dashboard') as SubArea;
  // FR-031a: Admin+Operator may manage plans/schedules/storage; Manager+Viewer
  // are read-only on the command center (no mutate buttons).
  const { isOperatorOrAbove } = useAuth();
  const canManage = isOperatorOrAbove();
  // The activity drawer is a single global mount (AppLayout); just toggle its store flag.
  // Spec 214 FR-026 (Q5): the badge counts IN-FLIGHT launches only — the old entries.length
  // was a 30-deep persisted launch history that read as "7 running backups" days later.
  const setActivityOpen = useBackupActivity((s) => s.setOpen);
  const activityCount = useBackupActivity(selectInflightCount);
  // Whole-page refresh: invalidate every query so all panels on the active tab refetch.
  // Manual (Refresh button) + automatic every 60s (operator request 2026-06-27).
  const qc = useQueryClient();
  const refreshAll = React.useCallback(() => { void qc.invalidateQueries(); }, [qc]);
  useEffect(() => {
    const id = setInterval(refreshAll, 60_000);
    return () => clearInterval(id);
  }, [refreshAll]);
  const [planForm, setPlanForm] = useState<{ plan: BackupPlanDetail | null } | null>(null);
  const openCreatePlan = () => setPlanForm({ plan: null });
  const openEditPlan = async (id: string) => {
    try {
      setPlanForm({ plan: await plansApi.get(id) });
    } catch {
      setPlanForm({ plan: null });
    }
  };

  // Short, user-facing guidance per sub-area (the typical end-to-end flow is:
  // Storage → grant SP → Plan (retention) → Schedule → Back up now → Restore; Dashboard
  // shows whether everything is protected and recoverable).
  const guidance: Record<SubArea, string> = {
    dashboard:
      'Command center: is everything protected and can we restore? Review coverage gaps and at-risk targets, then act on them.',
    plans:
      'A Backup Plan bundles one schedule + one storage + one retention; assign entities to it to protect them (on the Management tab). A lakehouse/warehouse belongs to exactly one plan. Select plans to bulk pause or legal-hold: pausing a plan pauses its backups, legal hold suspends retention. A plan can be deleted only when it has no running backups.',
    errors:
      'Backup, restore, copy, retention and verification errors that are STILL TRUE — the ACTUAL error detail, not a count. Anything a later successful run already fixed is not listed: this is a work queue, not a history. Each entry is attributed to a cause (configuration, platform limit, data issue, scale, transient, or a defect on our side) and carries an ordered remediation you can work through and record. Grouped by root cause by default, so one paused capacity failing many backups reads as one action.',
    management:
      'The entity → plan map: see which lakehouse/warehouse/workspace is protected by which plan. Assign unprotected entities to a plan, or move protected ones between plans — in bulk or per row. An entity belongs to exactly one plan; its schedule and storage come from that plan, and retention resolves source policy → plan policy → type default.',
    schedules:
      'Build a schedule visually (e.g. weekly full + daily incremental) — no cron required. Preview the next runs, then attach it to a plan.',
    restore:
      'Find a recovery point on the timeline and restore it into a NEW target (non-destructive). Not-restorable points show why. Use Test to prove recoverability without keeping the copy.',
    storage:
      'Manage backups in place: review each backup, run an ad-hoc backup, shift its start time, or move it to another plan. (Assign which entities a plan protects on the Management tab.)',
    health:
      'Backup health: every entity with no successful backup, each with a plain-language reason. Remediate online — exclude it (managed out of coverage, no SQL), repoint its storage to a reachable account, or retry. The Exclusions tab shows what is managed out and lets you re-include it.',
    recoverability:
      'Proof, not estimate: automated verification restores recovery points into throwaway targets to prove they recover, and maintains a per-asset score (at-risk = last failed or out of SLA). Secondary copies (3-2-1-1-0) and their lag show here too.',
    reports:
      'Download any of the 12 reports on demand (PDF/Excel), or schedule it to be generated on a cadence and emailed automatically — compliance, backup summary, recovery, failures, RPO, protection gaps, storage, retention & legal hold, audit, plan config, an executive summary, and the compliance evidence pack.',
    settings:
      'Define the central SLA building blocks: protection tiers (RPO + RTO in hours) and retention policies (how long copies are kept). Assign both per source on the Backups tab (per-row Tier and Retention); retention resolves source → plan → default.',
    cost:
      'See and control what backups cost. Per plan: WORM / verification / secondary-copy state, storage target, and the network route (cross-region and NAT paths drive egress). Toggle WORM, verification, and secondary copies globally or per plan; every option shows a directional cost note (never dollars). Egress offenders and money-saving suggestions surface here too.',
  };

  return (
    <PageLayout
      helpId="auto.backup-section"
      title="Backup Management"
      description="Plans, schedules, restore, storage, and protection health — one command center."
      icon={<Database />}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => setActivityOpen(true)}>
            <Activity className="h-4 w-4" /> Activity{activityCount > 0 ? ` (${activityCount})` : ''}
          </Button>
          <Button variant="outline" onClick={refreshAll} title="Refresh all data on this page (auto-refreshes every 60s)">
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
          {active === 'plans' && canManage && (
            <>
              <Button variant="outline" onClick={() => navigate('/backup/retention')}>
                Retention &amp; recycle bin
              </Button>
              <Button variant="primary" onClick={openCreatePlan}>
                New plan
              </Button>
            </>
          )}
        </div>
      }
    >
      <Tabs
        tabs={TABS}
        value={active}
        onChange={(key) => navigate(`/backup/${key}`)}
      />
      {/* User guidance for the active sub-area */}
      <div className="mt-3 flex items-start gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-3 py-2">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-accent)]" />
        <p className="text-sm text-[var(--color-text-secondary)]">{guidance[active]}</p>
      </div>
      <div className="mt-4">
        {active === 'plans' && <PlansArea onEdit={openEditPlan} />}
        {active === 'management' && <PlanAssignment />}
        {active === 'dashboard' && <CommandCenter />}
        {active === 'schedules' && <GraphicalScheduleBuilder />}
        {active === 'restore' && <RestoreBrowser />}
        {active === 'storage' && <StorageAssignment />}
        {active === 'health' && <BackupHealthArea />}
        {active === 'errors' && <BackupErrorsArea />}
        {active === 'recoverability' && <RecoverabilityArea />}
        {active === 'cost' && <CostControlsArea />}
        {active === 'reports' && <ReportsArea />}
        {active === 'settings' && (
          <div className="space-y-6">
            <ProtectionTiersSettings />
            <RetentionPoliciesSettings />
          </div>
        )}
      </div>
      <PlanFormModal open={!!planForm} plan={planForm?.plan ?? null} onClose={() => setPlanForm(null)} />
    </PageLayout>
  );
};

export default BackupSection;
